import {GROUND_BACKGROUNDS} from '../groundBackgrounds';

/**
 * ГЕЙЗЕРЫ И ПАРЯЩИЕ ИСТОЧНИКИ — эффект гейзерной подложки (geyser_1).
 *
 * На картинке нарисованы столбы гейзеров, источники и цветные озерца. Эффект
 * оживляет именно их (см. utils/effects/biome/geyserSteam):
 *  - столб гейзера «дышит»: по нему снизу вверх идут бледные клубы;
 *  - время от времени гейзер извергается: струя бьет ярче, шире и выше, от
 *    столба отрываются облака пара и уходят по ветру, струя уносит вверх
 *    водяную пыль;
 *  - над источниками и темными озерцами поднимаются струйки пара;
 *  - на самом поле то тут, то там из трещины в корке поднимается струйка пара
 *    с тенью на земле: тонкая у трещины, кверху она расходится конусом.
 *
 * ПОДЛОЖКА НАРИСОВАНА НЕ СВЕРХУ, А ПОД УГЛОМ: столбы гейзеров уходят к верху
 * картинки. Поэтому пар поднимается вверх по экрану, слегка расходясь веером от
 * середины картинки, — как нарисованные столбы.
 *
 * Середина картинки — светлая растрескавшаяся корка: белый пар на ней сам по
 * себе не виден. Гейзеры, источники и озерца — у кромки поля и вокруг него,
 * только на темном и цветном: на темных озерцах и на скалах за гейзерами. На
 * телефоне они в кадре, только когда игрок смотрит на край поля, поэтому на
 * самой корке парят трещины: струйку там выдает ее темная тень на земле.
 *
 * Размеры — в долях стороны картинки пейзажа: нарисованные гейзеры и озерца
 * растут и уменьшаются вместе с ней, а не вместе с ячейкой (ячейка — около
 * 0.03 стороны). Размер клуба — размер кадра его текстуры; видимый клуб
 * примерно на четверть меньше.
 */
export const GEYSER_STEAM_KIND = 'geyserSteam';

// Пар чуть теплее белого, как нарисованный; водяная пыль — белая
const STEAM_TINT = 0xfff6e8;
const SPRAY_TINT = 0xffffff;
// Тень пара на светлой корке: темная теплая, в тон трещинам
const STEAM_SHADOW_TINT = 0x4a3f36;

export const GEYSER_STEAM_EFFECT = Object.freeze({
  kind: GEYSER_STEAM_KIND,
  backgrounds: Object.freeze({
    [GROUND_BACKGROUNDS.GEYSER_1]: Object.freeze({
      // Куда поднимается пар источника в точке u: вектор (riseLean * (u - 0.5), -1) —
      // вверх по экрану и чуть в сторону от середины, как нарисованные столбы
      riseLean: 0.6,
      // Гейзер или источник «в кадре», если он не дальше стольких размеров
      // ячейки от того, что видит камера
      viewMarginFactor: 2,

      /**
       * Нарисованные гейзеры: основание и вершина столба, его ширина.
       * cloudHeight — на какой высоте столба (в его длинах; больше 1 — выше
       * вершины) от него отрываются облака пара при извержении: вершина
       * большого гейзера уходит в затемнение у края картинки, там облака не
       * было бы видно.
       * Малый гейзер у правого края (0.938, 0.374) пропущен: он целиком в
       * затемнении края картинки, пар там тускл.
       */
      geysers: [
        // Большой гейзер на террасах: белый столб на светлой дымке, извержение
        // видно слабее остальных
        {base: {u: 0.693, v: 0.176}, top: {u: 0.705, v: 0.032}, widthShare: 0.03, cloudHeight: {min: 0.42, max: 0.7}},
        // Гейзер перед темными базальтовыми столбами: виден лучше всех
        {base: {u: 0.833, v: 0.203}, top: {u: 0.852, v: 0.124}, widthShare: 0.022, cloudHeight: {min: 1.05, max: 1.5}},
        // Гейзер на западе, у самого затемнения края
        {base: {u: 0.076, v: 0.352}, top: {u: 0.056, v: 0.268}, widthShare: 0.02, cloudHeight: {min: 0.5, max: 0.9}},
      ],

      // Дыхание столба между извержениями: клубы один за другим поднимаются от
      // основания к вершине. Бледно: столб уже нарисован, яркий пар поверх него
      // двоил бы картинку
      columns: {
        puffCount: 6,
        // За сколько клуб проходит столб
        riseMs: {min: 5200, max: 7600},
        alpha: {min: 0.14, max: 0.26},
        // Клуб проступает у основания и тает к вершине (доли пути)
        fadeInShare: 0.25,
        fadeOutShare: 0.4,
        // Ширина клуба в ширинах столба: у основания и у вершины; клуб вытянут
        // вдоль столба во столько раз и наклонен не больше чем на tiltDeg
        widthScale: {from: 1.1, to: 1.7},
        stretch: 1.4,
        tiltDeg: 14,
        // Клуб на каждом круге уходит чуть вбок, ширин столба
        swayShare: 0.5,
        // Столб дышит: его пар то плотнее, то реже
        breathMs: {min: 6000, max: 9000},
        breathDepth: 0.45,
        tint: STEAM_TINT,
      },

      // Извержение: нарастание, струя, спад; от столба отрываются облака
      eruption: {
        firstDelayMs: {min: 4000, max: 9000},
        // Пауза — от начала предыдущего извержения; разом извергаются не больше двух
        intervalMs: {min: 9000, max: 17000},
        maxGroups: 2,
        // Гейзер, на который смотрит камера, извергается во столько раз охотнее
        inViewWeight: 4,
        buildUpMs: 1500,
        jetMs: {min: 4000, max: 5000},
        settleMs: 1200,
        jet: {
          puffCount: 7,
          // За сколько клуб струи пролетает столб: струя бьет быстро
          riseMs: {min: 900, max: 1300},
          // Плотно: белая струя на белом нарисованном столбе иначе не видна
          alpha: {min: 0.45, max: 0.8},
          // Клуб проступает не у самого основания: низ нарисованной струи
          // остается четким, пар клубится выше
          fadeInShare: 0.3,
          fadeOutShare: 0.35,
          // Струя начинается с такой доли высоты столба и вырастает выше него
          startReach: 0.35,
          reach: 1.15,
          // Струя шире нарисованного столба и расходится кверху: извержение
          // видно по краям столба, на скалах и террасах за ним. Клуб вытянут
          // вдоль струи: круглый читался бы шариком
          widthScale: {from: 1.1, to: 2.7},
          stretch: 1.6,
          tiltDeg: 10,
          // Разброс клубов вбок у вершины, ширин столба
          spreadShare: 1.3,
          // Струя бьет толчками
          pulseMs: 800,
          pulseDepth: 0.07,
          tint: STEAM_TINT,
        },
        cloud: {
          count: 4,
          // Облака отрываются одно за другим на протяжении струи (доли ее срока)
          bornShare: {min: 0.15, max: 0.95},
          lifeMs: {min: 4500, max: 6500},
          // Размер кадра: от и до, доли стороны картинки (1.3 -> 3 ячейки)
          sizeShare: {from: 0.04, to: 0.09},
          // Быстро разбухает и поднимается, потом только плывет: 1 - (1 - t)^power
          easePower: 2,
          // Еще поднимается вдоль столба и уходит по ветру матча, доли стороны
          liftShare: {min: 0.012, max: 0.03},
          driftShare: {min: 0.03, max: 0.055},
          // Разброс места рождения поперек столба, его ширин
          jitterShare: 0.6,
          alpha: 0.7,
          fadeInShare: 0.14,
          fadePower: 1.3,
          spinDegPerSecond: 6,
          tint: STEAM_TINT,
        },
        // Водяная пыль: струя уносит мелкие капли вверх вдоль столба. Капля
        // летит только вверх, замедляется и тает на лету, еще поднимаясь. Дугой
        // в сторону и обратно вниз она не падает: так пыль читалась фонтанчиком
        spray: {
          count: {min: 8, max: 12},
          lifeMs: {min: 900, max: 1300},
          // Кадр капли, доли стороны; видимая капля вдвое меньше. Капля
          // вытянута вдоль своего полета
          sizeShare: 0.01,
          stretch: 1.6,
          alpha: 0.85,
          // Проступает быстро и тает весь остаток полета: (1 - t)^fadePower
          fadeInShare: 0.12,
          fadePower: 1.2,
          // С какой высоты струи срывается капля: доли высоты, до которой
          // струя бьет в этот миг
          startShare: {min: 0.3, max: 0.8},
          // На сколько капля поднимается за полет, длин столба. Замедляется:
          // 1 - (1 - t)^slowPower — к концу полета почти стоит
          riseShare: {min: 0.3, max: 0.55},
          slowPower: 2,
          // Потолок, длин столба от его основания: пыль тает у вершины струи
          // (струя бьет до 1.15-1.23 длины столба), в небо не улетает
          ceilingShare: 1.3,
          // Где капля появляется, ширин столба от его оси: у кромки
          // разошедшейся струи, а не в ее середине — белой капли на белом не видно
          startOutShare: {min: 0.4, max: 1.15},
          // Пыль расходится веером: капля уходит от оси столба на такую долю
          // своего подъема
          fanShare: {min: 0.02, max: 0.22},
          // Пыль летит, пока струя не слабее этой доли своей силы, и
          // набирает полную яркость еще через strengthFade
          minStrength: 0.35,
          strengthFade: 0.25,
          tint: SPRAY_TINT,
        },
      },

      // Струйки пара над источниками и темными озерцами
      springs: {
        firstDelayMs: {min: 200, max: 600},
        intervalMs: {min: 200, max: 420},
        // Столько источников парит разом; первые струйки рождаются сразу
        maxGroups: 12,
        prewarm: 10,
        // Источник, на который смотрит камера, парит во столько раз охотнее:
        // то, что в кадре, парит почти без перерывов
        inViewWeight: 12,
        // Пар клонится по ветру матча: на столько долей своего подъема
        windLean: 0.35,
        // Озерцо нарисовано под углом: по вертикали оно во столько раз уже;
        // клубы рождаются в средней части озерца (доля его радиуса)
        flatten: 0.55,
        spawnScale: 0.6,
        // Клуб вытянут вверх и повернут как придется: струйка, а не шарик
        stretch: 1.3,
        tiltDeg: 30,
        // Клуб проступает у самой воды и долго тает (доли срока)
        fadeInShare: 0.18,
        fadeOutShare: 0.55,
        // Подъем замедляется: 1 - (1 - t)^climbPower; клуб разбухает как t^growPower
        climbPower: 1.3,
        growPower: 0.7,
        // Клубы одной струйки разного размера (доля наибольшего)
        sizeJitter: {min: 0.8, max: 1},
        tint: STEAM_TINT,
        // Струйка — три клуба один за другим, внахлест: каждый полупрозрачен,
        // вместе они дают мягкий пар с плотной серединой.
        // Источник: узкая высокая струйка. Оба источника лежат в затемнении
        // края картинки, поэтому плотнее озерных
        vent: {
          puffCount: 3,
          staggerMs: {min: 500, max: 900},
          lifeMs: {min: 2500, max: 4000},
          riseShare: {min: 0.03, max: 0.05},
          sizeShare: {from: 0.022, to: 0.05},
          alpha: 0.8,
        },
        // Озерцо: клубы пониже и пошире. Бледнее 0.6 пар на цветной воде едва виден
        pool: {
          puffCount: 3,
          staggerMs: {min: 450, max: 900},
          lifeMs: {min: 2800, max: 3800},
          riseShare: {min: 0.03, max: 0.045},
          sizeShare: {from: 0.024, to: 0.058},
          alpha: 0.62,
        },
        /**
         * Источники и озерца на картинке (доли стороны), radius — радиус
         * озерца. Только темные и цветные: на бледных (розовое и охристое
         * озерца террас, серный холм и источник на севере) белый пар не виден.
         */
        anchors: [
          // Парящий конус на юго-востоке и источник на западе
          {kind: 'vent', u: 0.928, v: 0.646, radius: 0.004},
          {kind: 'vent', u: 0.085, v: 0.532, radius: 0.004},
          // Серые и темные озерца на северо-западе и западе
          {kind: 'pool', u: 0.24, v: 0.244, radius: 0.02},
          {kind: 'pool', u: 0.166, v: 0.376, radius: 0.013},
          {kind: 'pool', u: 0.164, v: 0.44, radius: 0.013},
          // Желто-зеленое озерцо террас и бирюзовое под гейзером
          {kind: 'pool', u: 0.702, v: 0.301, radius: 0.02},
          {kind: 'pool', u: 0.8, v: 0.23, radius: 0.025},
          // Озерца на юго-западе
          {kind: 'pool', u: 0.235, v: 0.612, radius: 0.012},
          {kind: 'pool', u: 0.255, v: 0.69, radius: 0.014},
          {kind: 'pool', u: 0.175, v: 0.712, radius: 0.018},
          // Оливковые озерца на юго-востоке
          {kind: 'pool', u: 0.79, v: 0.745, radius: 0.028},
          {kind: 'pool', u: 0.771, v: 0.698, radius: 0.011},
          // Охристое, желтое и темно-красное озерца на юге, грязевой котел
          {kind: 'pool', u: 0.67, v: 0.81, radius: 0.012},
          {kind: 'pool', u: 0.564, v: 0.865, radius: 0.02},
          {kind: 'pool', u: 0.66, v: 0.89, radius: 0.03},
          {kind: 'pool', u: 0.325, v: 0.82, radius: 0.02},
        ],
      },

      /**
       * Трещины корки на самом поле: струйка пара поднимается там, куда
       * смотрит камера. Пар выходит из тонкой жилы: клуб рождается у трещины
       * точкой и ширится, пока поднимается. Клубы струйки, идущие один за
       * другим внахлест, складываются в КОНУС с вершиной в трещине: тонкий у
       * земли, широкий наверху. Ширина клуба — по его высоте над трещиной, а
       * не по возрасту: какой бы клуб ни оказался выше, он шире.
       * Белый пар на светлой корке не виден — струйку выдает тень: под каждым
       * клубом на земле лежит темное пятно, клуб поднимается от него вверх по
       * экрану. Слой — над дымкой пустых ячеек, под жетонами: строение или
       * гору пар не закрывает.
       * Размеры здесь — в размерах ячейки: трещины не нарисованы, и струйка
       * должна читаться рядом с жетоном при любом размере поля.
       */
      fumaroles: {
        // Одна струйка парит с первого кадра (prewarm), следующая — не раньше
        // чем через три секунды: шипение первой к этому мигу уже отзвучало
        firstDelayMs: {min: 3000, max: 4500},
        // Струйка живет около пяти секунд, новая — раз в три-четыре с половиной
        // секунды: разом парят одна-две трещины. Было втрое чаще (раз в
        // секунду с небольшим, три-четыре трещины разом): владелец игры
        // попросил реже — и чтобы шипела каждая. Поэтому новая струйка не
        // раньше чем через три секунды после прежней: самое долгое шипение
        // (две с половиной секунды) к этому мигу уже отзвучало
        intervalMs: {min: 3000, max: 4500},
        maxGroups: 2,
        prewarm: 1,
        // Только над игровыми ячейками: доля покрытия поля, с которой место годится
        minCoverage: 0.7,
        // Струйка — пять клубов один за другим, внахлест: клубы у трещины
        // мелкие, и между тремя конус рвался
        puffCount: 5,
        staggerMs: {min: 380, max: 560},
        lifeMs: {min: 2800, max: 3600},
        // Высота струйки, размеров ячейки; по ветру матча струйка клонится на
        // такую долю своей высоты
        riseFactor: {min: 1.5, max: 2.1},
        windLean: 0.5,
        // Кадр клуба: у трещины и на вершине струйки, размеров ячейки (видимый
        // клуб на четверть меньше кадра). У трещины — точка: пар выходит из
        // тонкой жилы; на вершине — три четверти ширины ячейки
        sizeFactor: {from: 0.15, to: 2},
        // Струйки разной ширины (доля наибольшей). У клубов одной струйки
        // ширина общая: иначе конус рассыпался бы
        sizeJitter: {min: 0.85, max: 1},
        // Клуб уходит от оси струйки вбок: у трещины — нисколько, выше — на
        // такую долю того, на сколько конус там уже разошелся
        scatterShare: 0.2,
        // Клуб вытянут вдоль струйки: у трещины — тонкий штрих (жила), на
        // вершине — почти круглый. Повернут как придется, но тем сильнее, чем
        // выше: у трещины штрих смотрит точно вдоль струйки
        stretch: {from: 2, to: 1.25},
        tiltDeg: 30,
        // Полупрозрачен: плотнее пар выходил слишком белым (замечание владельца
        // игры), а клубы еще и ложатся внахлест
        alpha: 0.56,
        // Проступает у самой трещины (тонкое основание конуса видно) и долго тает
        fadeInShare: 0.08,
        fadeOutShare: 0.55,
        // Подъем замедляется: 1 - (1 - t)^climbPower. Ширина клуба — по доле
        // пройденной высоты в степени growPower (1 — прямой конус)
        climbPower: 1.4,
        growPower: 1,
        tint: STEAM_TINT,
        // Тень клуба на земле: остается у трещины, расплывается вместе с клубом
        // (сначала — точка) и сдвигается вниз по экрану тем дальше, чем выше
        // клуб. Бледная, под стать полупрозрачному пару: тени клубов тоже
        // ложатся внахлест и вместе темнее каждой
        shadow: {
          tint: STEAM_SHADOW_TINT,
          alpha: 0.3,
          // Шире клуба и сплющена: тень лежит на земле, которая видна под углом
          widthScale: 1.15,
          flatten: 0.6,
          // Сдвиг вниз по экрану в конце подъема, размеров ячейки
          dropFactor: 0.25,
        },
      },
    }),
  }),
});
